/**
 * Background preloading for open workspace tabs (Figma-style).
 *
 * Mounts one renderless preloader per open canvas tab. Each preloader keeps
 * the canvas REST record warm and — for the active tab plus the most recently
 * used tabs (ROOM_HOLD_CAP total) — holds a Liveblocks room lease open via
 * `client.enterRoom`, which is reference-counted per room id. Because the
 * canvas page's RoomProvider enters the same room id, it mounts onto the
 * already-connected, storage-loaded room instance: every loading gate passes
 * on first render and switching tabs is near-instant.
 *
 * The double-lease on the active tab is intentional: when the route's
 * RoomProvider unmounts on switch-away, the preloader lease keeps the room
 * alive (and live) in the background.
 *
 * Per-tab status is exposed to the tab strip via `useTabPreloadStatus` so
 * tabs can show a loading indicator while their room loads — including the
 * automatic reconnect + storage resync after the browser tab was hidden long
 * enough for `backgroundKeepAliveTimeout` to park the sockets.
 *
 * Chat tabs are intentionally not preloaded: they render fast from the
 * react-query cache and use SSE, not Liveblocks.
 */

import { useCanvasById } from "@/api/canvas";
import { ROUTES } from "@/constants/routes";
import { useIsCanvasCentricApp } from "@/context/CanvasCentricContext";
import { useOpenTabs } from "@/context/OpenTabsContext";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { CANVAS_INITIAL_PRESENCE } from "@/lib/liveblocks/canvasPresence";
import { useClient } from "@liveblocks/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useMatch } from "react-router-dom";

export type TabPreloadStatus = "loading" | "ready" | "error";

/**
 * Rooms held open concurrently (active + most recent others). Each held room
 * is a live websocket, an in-memory storage doc, and a presence entry, and
 * counts against the Liveblocks concurrent-connection quota — dial down here
 * if that bites.
 */
const ROOM_HOLD_CAP = 8;

/** Concurrent storage loads; staggers token POSTs + fetches on cold start. */
const STORAGE_LOAD_CONCURRENCY = 3;

const StatusContext = createContext<ReadonlyMap<
  string,
  TabPreloadStatus
> | null>(null);

interface TabPreloadActions {
  setStatus: (tabId: string, status: TabPreloadStatus) => void;
  clearStatus: (tabId: string) => void;
  /** Admission control for room storage loads; returns an idempotent release. */
  acquireSlot: () => Promise<() => void>;
}

const ActionsContext = createContext<TabPreloadActions | null>(null);

/**
 * Preload status for a tab, or undefined when the tab isn't preloaded (chat
 * tabs, missing provider). Undefined should render as the normal tab icon.
 */
export function useTabPreloadStatus(
  tabId: string,
): TabPreloadStatus | undefined {
  return useContext(StatusContext)?.get(tabId);
}

function createSlotQueue(limit: number) {
  let active = 0;
  const waiters: Array<() => void> = [];
  const release = () => {
    const next = waiters.shift();
    if (next) {
      next(); // hand the slot over; `active` already counts it
    } else {
      active -= 1;
    }
  };
  return async (): Promise<(() => void) | undefined> => {
    if (active >= limit) {
      await new Promise<void>((resolve) => waiters.push(resolve));
    } else {
      active += 1;
    }
    let released = false;
    return () => {
      if (released) return;
      released = true;
      release();
    };
  };
}

export function TabPreloadProvider({ children }: { children: ReactNode }) {
  const { openTabs } = useOpenTabs();
  const lg = useBreakpoint("lg");
  // The workspace tab bar (and its instant-switch UX) only exists in the
  // canvas-centric app; in the legacy layout, persisted tabs must not hold
  // background rooms nobody can see or switch to.
  const { isCanvasCentricApp } = useIsCanvasCentricApp();

  // Same active-tab derivation as useWorkspaceTabBarState.
  const canvasMatch = useMatch(ROUTES.CANVAS.DETAIL);
  const chatMatch = useMatch(ROUTES.CHAT.DETAIL);
  const activeId =
    canvasMatch?.params?.canvasId ?? chatMatch?.params?.flowSessionId ?? null;

  // Most-recently-active tab ids, most recent first.
  const [mruOrder, setMruOrder] = useState<string[]>([]);
  useEffect(() => {
    if (!activeId) return;
    setMruOrder((prev) =>
      prev[0] === activeId
        ? prev
        : [activeId, ...prev.filter((id) => id !== activeId)],
    );
  }, [activeId]);

  const canvasTabIds = useMemo(
    () => openTabs.filter((t) => t.kind === "canvas").map((t) => t.id),
    [openTabs],
  );

  // Rooms are only held where the tab bar exists; on mobile the REST cache is
  // still kept warm but we don't pay for background sockets.
  const heldIds = useMemo(() => {
    const held = new Set<string>();
    if (!lg) return held;
    const open = new Set(canvasTabIds);
    if (activeId && open.has(activeId)) held.add(activeId);
    for (const id of mruOrder) {
      if (held.size >= ROOM_HOLD_CAP) break;
      if (open.has(id)) held.add(id);
    }
    // Top up with tabs never activated this session, most recently opened
    // (rightmost) first — addTab appends.
    for (let i = canvasTabIds.length - 1; i >= 0; i--) {
      if (held.size >= ROOM_HOLD_CAP) break;
      held.add(canvasTabIds[i]);
    }
    return held;
  }, [lg, activeId, mruOrder, canvasTabIds]);

  const [statuses, setStatuses] = useState<
    ReadonlyMap<string, TabPreloadStatus>
  >(() => new Map());
  const setStatus = useCallback((tabId: string, status: TabPreloadStatus) => {
    setStatuses((prev) => {
      if (prev.get(tabId) === status) return prev;
      const next = new Map(prev);
      next.set(tabId, status);
      return next;
    });
  }, []);
  const clearStatus = useCallback((tabId: string) => {
    setStatuses((prev) => {
      if (!prev.has(tabId)) return prev;
      const next = new Map(prev);
      next.delete(tabId);
      return next;
    });
  }, []);

  const [acquireFromQueue] = useState(() =>
    createSlotQueue(STORAGE_LOAD_CONCURRENCY),
  );
  const actions = useMemo<TabPreloadActions>(
    () => ({
      setStatus,
      clearStatus,
      acquireSlot: () =>
        acquireFromQueue().then((release) => release ?? (() => {})),
    }),
    [setStatus, clearStatus, acquireFromQueue],
  );

  return (
    <ActionsContext.Provider value={actions}>
      <StatusContext.Provider value={statuses}>
        {children}
        {isCanvasCentricApp &&
          openTabs.map((tab) =>
            tab.kind === "canvas" ? (
              <CanvasTabPreload
                key={tab.id}
                canvasId={tab.id}
                holdRoom={heldIds.has(tab.id)}
                isActive={tab.id === activeId}
              />
            ) : null,
          )}
      </StatusContext.Provider>
    </ActionsContext.Provider>
  );
}

function CanvasTabPreload({
  canvasId,
  holdRoom,
  isActive,
}: {
  canvasId: string;
  holdRoom: boolean;
  isActive: boolean;
}) {
  const client = useClient();
  const actions = useContext(ActionsContext);
  if (!actions) {
    throw new Error("CanvasTabPreload must be inside TabPreloadProvider");
  }
  const { setStatus, clearStatus, acquireSlot } = actions;

  // Subscribing keeps ["canvas", id] warm (and refetching on window focus)
  // for as long as the tab is open, and yields the room id for the hold.
  const canvasQuery = useCanvasById(canvasId);
  const roomId = canvasQuery.data?.liveblocks_room_id?.trim() || null;

  const [roomPhase, setRoomPhase] = useState<TabPreloadStatus>("loading");

  // The active tab's storage load must not wait behind background preloads
  // (its RoomProvider drives it anyway), so it bypasses the slot queue. Kept
  // in a ref so activation changes don't re-run the lease effect: dropping
  // and re-entering the room on every switch would tear down the very
  // connection this component exists to preserve.
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;

  useEffect(() => {
    if (!holdRoom || !roomId) {
      setRoomPhase("loading");
      return;
    }
    let cancelled = false;
    let cleanup: (() => void) | undefined;
    (async () => {
      let release = isActiveRef.current ? undefined : await acquireSlot();
      if (cancelled) {
        release?.();
        return;
      }
      // Activated while queued: the route's RoomProvider is already driving
      // this room's connect + storage fetch — don't hold a slot for work in
      // flight elsewhere. Still enter below: the lease is the keep-alive.
      if (release && isActiveRef.current) {
        release();
        release = undefined;
      }
      const { room, leave } = client.enterRoom(roomId, {
        initialPresence: CANVAS_INITIAL_PRESENCE,
      });
      const update = () => {
        const status = room.getStatus();
        if (status === "disconnected") {
          // Terminal (e.g. auth returned forbidden) — don't hold a slot.
          release?.();
          setRoomPhase("error");
          return;
        }
        const storage = room.getStorageStatus();
        setRoomPhase(
          status === "connected" &&
            (storage === "synchronizing" || storage === "synchronized")
            ? "ready"
            : "loading",
        );
      };
      const unsubStatus = room.subscribe("status", update);
      const unsubStorage = room.subscribe("storage-status", update);
      // Storage is lazy — this call is what actually triggers the load.
      room
        .waitUntilStorageReady()
        .catch(() => {})
        .finally(() => release?.());
      update();
      cleanup = () => {
        unsubStatus();
        unsubStorage();
        release?.();
        leave();
      };
      if (cancelled) {
        cleanup();
        cleanup = undefined;
      }
    })();
    return () => {
      cancelled = true;
      cleanup?.();
      cleanup = undefined;
    };
  }, [holdRoom, roomId, client, acquireSlot]);

  const status: TabPreloadStatus = canvasQuery.isError
    ? "error"
    : canvasQuery.isPending
      ? "loading"
      : !roomId
        ? "error"
        : holdRoom
          ? roomPhase
          : // Beyond the hold cap: REST is warm, the room loads on open — a
            // permanent spinner would be worse than none.
            "ready";

  useEffect(() => {
    setStatus(canvasId, status);
  }, [canvasId, status, setStatus]);
  useEffect(() => () => clearStatus(canvasId), [canvasId, clearStatus]);

  return null;
}
